import React, { useState } from "react";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Textarea } from "../ui/textarea";
import { errorMessage, formsApi } from "../../lib/api";
import { FORM_TYPES, cleanEmails, splitEmails } from "../../lib/forms";

const inputCls = "h-10 rounded-none border-border bg-transparent px-3 text-sm focus-visible:ring-1 focus-visible:ring-primary focus-visible:ring-offset-0";
const areaCls = "rounded-none border-border bg-transparent px-3 py-2 text-sm focus-visible:ring-1 focus-visible:ring-primary focus-visible:ring-offset-0";

const Field = ({ label, hint, children }) => (
  <div className="space-y-1.5">
    <Label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">{label}</Label>
    {children}
    {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
  </div>
);

const Toggle = ({ label, checked, onChange, testId }) => (
  <label className="flex cursor-pointer items-center justify-between gap-4 border border-border px-4 py-3">
    <span className="text-sm font-semibold">{label}</span>
    <Switch checked={checked} onCheckedChange={onChange} data-testid={testId} />
  </label>
);

const PLACEHOLDERS = "{first_name} {name} {email} {phone} {message} {form_label} {page_title} {studio}";

/** One card per form: who gets notified + the automatic follow-up the submitter receives. */
const FormSettingsCard = ({ settings, onSaved }) => {
  const meta = FORM_TYPES.find((f) => f.id === settings.form_type);
  const [form, setForm] = useState({ ...settings, recipientsText: settings.recipients.join(", ") });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      const { recipientsText, form_type, updated_at, ...rest } = form;
      const saved = await formsApi.updateSettings(form_type, { ...rest, recipients: cleanEmails(splitEmails(recipientsText)) });
      setForm({ ...saved, recipientsText: saved.recipients.join(", ") });
      onSaved(saved);
      toast.success(`${saved.label} settings saved`);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="border border-border bg-card" data-testid={`form-settings-${settings.form_type}`}>
      <header className="border-b border-border px-6 py-5">
        <p className="eyebrow">{meta?.label}</p>
        <h2 className="display mt-2 text-3xl uppercase leading-none tracking-wide">{form.label}</h2>
        <p className="mt-1 text-sm text-muted-foreground">{meta?.description}</p>
      </header>

      <div className="grid gap-8 px-6 py-6 lg:grid-cols-2">
        <div className="space-y-4">
          <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-primary">Where it goes</p>
          <Field label="Form name (shown in emails)">
            <Input value={form.label} onChange={(e) => set("label", e.target.value)} className={inputCls} data-testid={`fs-label-${settings.form_type}`} />
          </Field>
          <Field label="Send notifications to" hint="Comma-separated. Lead forms on landing pages can add extra recipients per block.">
            <Textarea value={form.recipientsText} onChange={(e) => set("recipientsText", e.target.value)} placeholder="owner@avalontattoo.ca, frontdesk@avalontattoo.ca" className={`${areaCls} min-h-[70px]`} data-testid={`fs-recipients-${settings.form_type}`} />
          </Field>
          <Toggle label="Email the team when a form comes in" checked={form.notify_enabled} onChange={(v) => set("notify_enabled", v)} testId={`fs-notify-${settings.form_type}`} />
          <Field label="Notification subject">
            <Input value={form.notify_subject} onChange={(e) => set("notify_subject", e.target.value)} className={inputCls} data-testid={`fs-notify-subject-${settings.form_type}`} />
          </Field>
        </div>

        <div className="space-y-4">
          <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-primary">Automatic follow-up to the sender</p>
          <Toggle label="Send an automatic reply" checked={form.autoreply_enabled} onChange={(v) => set("autoreply_enabled", v)} testId={`fs-autoreply-${settings.form_type}`} />
          <Field label="Subject">
            <Input value={form.autoreply_subject} onChange={(e) => set("autoreply_subject", e.target.value)} className={inputCls} data-testid={`fs-autoreply-subject-${settings.form_type}`} />
          </Field>
          <Field label="Message" hint={`Placeholders: ${PLACEHOLDERS}. Blank line = new paragraph. "Hi {first_name}," is added automatically.`}>
            <Textarea value={form.autoreply_body} onChange={(e) => set("autoreply_body", e.target.value)} className={`${areaCls} min-h-[170px]`} data-testid={`fs-autoreply-body-${settings.form_type}`} />
          </Field>
          <Field label="Replies go to (optional)" hint="Leave blank to use the sending address.">
            <Input value={form.reply_to} onChange={(e) => set("reply_to", e.target.value)} placeholder="hello@avalontattoo.ca" className={inputCls} data-testid={`fs-reply-to-${settings.form_type}`} />
          </Field>
        </div>
      </div>

      <footer className="flex items-center justify-end border-t border-border px-6 py-4">
        <Button onClick={save} disabled={busy} className="h-10 rounded-none px-6 text-[11px] font-semibold uppercase tracking-[0.2em]" data-testid={`fs-save-${settings.form_type}`}>
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Save {meta?.label.toLowerCase()} settings
        </Button>
      </footer>
    </section>
  );
};

export default FormSettingsCard;
